// Shared building blocks of the Usage dashboard pages.
//
// Extracted so UsageDashboard (usage & cost) and UserAnalytics (Safety Dashboard
// product analytics) look like one dashboard rather than two that happen to sit
// on the same screen — same section chrome, same chart styling, same formatting.
import React, { useState } from 'react';

import { authHeaders } from '../../utils/authHeader';

export const AXIS_TICK = { fill: '#94a3b8', fontSize: 12 };
export const SMALL_AXIS_TICK = { fill: '#94a3b8', fontSize: 10 };
export const TOOLTIP_STYLE = {
  backgroundColor: '#09101b',
  border: '1px solid #334155',
  borderRadius: '0.5rem',
  color: '#e2e8f0',
};
export const TOOLTIP_CURSOR = { fill: 'rgba(59, 130, 246, 0.08)' };
export const LEGEND_STYLE = { color: '#cbd5e1' };

// Long project / user / feature names are unreadable on a rotated x-axis, so the
// category bars are horizontal: the label lane is wide, left-aligned and never
// rotated. Values longer than the lane are ellipsised — the tooltip always shows
// the full name, so nothing is silently lost.
export const CAT_LANE = 168;
export const shortLabel = (v) => {
  const t = String(v ?? '—');
  return t.length > 26 ? `${t.slice(0, 25)}…` : t;
};
export const catChartHeight = (rows) =>
  Math.max(180, Math.min(460, (rows?.length || 0) * 28 + 48));

export function fmtDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

export function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(); // browser-local from UTC (doc §14.4)
  } catch {
    return iso;
  }
}

export async function getJson(url) {
  const res = await fetch(url, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** A headline figure. `hint` is the small line under the label — use it to say
 *  what the number is measured over, never to hide a caveat. */
export const Tile = ({ label, value, hint }) => (
  <div className="usage-tile">
    <div className="tile-value">{value}</div>
    <div className="tile-label">{label}</div>
    {hint && <div className="usage-tile-hint">{hint}</div>}
  </div>
);

/** Collapsible section card; open/closed persisted per section in localStorage. */
export const SectionCard = ({ id, title, meta, defaultOpen = true, children }) => {
  const storageKey = `rsaUsage:section:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === null ? defaultOpen : saved === '1';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () => {
    setOpen((o) => {
      try { localStorage.setItem(storageKey, o ? '0' : '1'); } catch { /* ok */ }
      return !o;
    });
  };
  return (
    <div className="usage-section">
      <button type="button" className="usage-section-head" onClick={toggle}
        aria-expanded={open}>
        <span className={`usage-section-chevron ${open ? 'open' : ''}`}>▸</span>
        <span className="usage-section-title">{title}</span>
        {meta && <span className="usage-section-meta">{meta}</span>}
      </button>
      {open && <div className="usage-section-body">{children}</div>}
    </div>
  );
};
